import { useState, useMemo } from 'react';
import { Shield, Search, Check, X, AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/toaster';
import { normalizeError } from '@/lib/api';
import { usePermissionMatrix, useUpdateRoleCapability } from '@/hooks/use-permissions';
import type { GlobalRole } from '@/types';

const ROLE_ORDER: GlobalRole[] = ['admin', 'supervisor', 'user', 'client'];

const ROLE_LABELS: Record<GlobalRole, string> = {
  admin: 'Admin',
  supervisor: 'Supervisor',
  user: 'User',
  client: 'Client',
};

const ROLE_COLORS: Record<GlobalRole, string> = {
  admin: 'bg-primary text-primary-foreground',
  supervisor: 'bg-purple-500/20 text-purple-500',
  user: 'bg-blue-500/20 text-blue-500',
  client: 'bg-orange-500/20 text-orange-500',
};

const CATEGORY_ORDER = [
  'Users',
  'Teams',
  'Projects',
  'Lists',
  'Tasks',
  'Statuses',
  'Tags',
  'Comments',
  'Attachments',
  'Chats',
  'Settings',
  'Permissions',
  'Audit',
];

/** Section id prefix for quick-nav scroll targets */
function sectionId(category: string): string {
  return `section-${category.toLowerCase().replace(/\s+/g, '-')}`;
}

interface PendingChange {
  role: GlobalRole;
  capabilityKey: string;
  allowed: boolean;
  isProtected: boolean;
}

export function PermissionsPage() {
  const { data: permissionMatrix, isLoading } = usePermissionMatrix();
  const updateCapability = useUpdateRoleCapability();
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null);

  // Group capabilities by category
  const groupedCapabilities = useMemo(() => {
    if (!permissionMatrix) return {};

    const groups: Record<string, typeof permissionMatrix.capabilities> = {};
    
    for (const cap of permissionMatrix.capabilities) {
      if (!groups[cap.category]) {
        groups[cap.category] = [];
      }
      groups[cap.category].push(cap);
    }

    return groups;
  }, [permissionMatrix]);

  // Filter capabilities by search
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return groupedCapabilities;

    const query = searchQuery.toLowerCase();
    const result: typeof groupedCapabilities = {};

    for (const [category, caps] of Object.entries(groupedCapabilities)) {
      const filtered = caps.filter(
        (cap) =>
          cap.key.toLowerCase().includes(query) ||
          cap.label.toLowerCase().includes(query) ||
          cap.description?.toLowerCase().includes(query)
      );
      if (filtered.length > 0) {
        result[category] = filtered;
      }
    }

    return result;
  }, [groupedCapabilities, searchQuery]);

  // Sort categories
  const sortedCategories = useMemo(() => {
    return Object.keys(filteredGroups).sort((a, b) => {
      const aIndex = CATEGORY_ORDER.indexOf(a);
      const bIndex = CATEGORY_ORDER.indexOf(b);
      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  }, [filteredGroups]);

  const handleToggle = (role: GlobalRole, capabilityKey: string, currentAllowed: boolean, isProtected: boolean) => {
    const newAllowed = !currentAllowed;

    // Check for protected capability being disabled for admin
    if (isProtected && role === 'admin' && !newAllowed) {
      setPendingChange({ role, capabilityKey, allowed: newAllowed, isProtected: true });
      return;
    }

    // For other changes, apply immediately
    applyChange(role, capabilityKey, newAllowed);
  };

  const applyChange = async (role: GlobalRole, capabilityKey: string, allowed: boolean) => {
    try {
      await updateCapability.mutateAsync({ role, capabilityKey, allowed });
      toast({
        title: 'Permission updated',
        description: `${capabilityKey} is now ${allowed ? 'enabled' : 'disabled'} for ${ROLE_LABELS[role]}`,
      });
    } catch (error: unknown) {
      toast({
        title: 'Failed to update permission',
        description: normalizeError(error).message,
        variant: 'destructive',
      });
    }
  };

  const confirmPendingChange = () => {
    if (pendingChange) {
      applyChange(pendingChange.role, pendingChange.capabilityKey, pendingChange.allowed);
      setPendingChange(null);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64 mt-2" />
          </div>
        </div>
        <Skeleton className="h-10 w-full max-w-sm" />
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!permissionMatrix) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6">
        <Shield className="h-12 w-12 text-muted-foreground/50" />
        <p className="text-muted-foreground">Unable to load permissions</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header - fixed */}
      <div className="flex-shrink-0 p-6 pb-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="h-6 w-6" />
              Permissions
            </h1>
            <p className="text-muted-foreground">
              Manage role-based capabilities for your organization
            </p>
          </div>
        </div>

        {/* Quick navigation to matrix sections */}
        <div className="flex flex-wrap gap-1.5">
          {sortedCategories.map((category) => (
            <a
              key={category}
              href={`#${sectionId(category)}`}
              className="text-xs px-2.5 py-1.5 rounded-md bg-muted/60 hover:bg-muted text-foreground transition-colors"
            >
              {category}
            </a>
          ))}
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search capabilities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Matrix */}
      <div className="flex-1 flex flex-col overflow-hidden mx-6 mb-6 border rounded-lg">
        {/* Header Row - frozen */}
        <div className="flex-shrink-0 grid grid-cols-[1fr_100px_100px_100px_100px] bg-muted/50 border-b">
          <div className="p-3 font-medium text-sm">Capability</div>
          {ROLE_ORDER.map((role) => (
            <div key={role} className="p-3 text-center">
              <Badge className={ROLE_COLORS[role]}>{ROLE_LABELS[role]}</Badge>
            </div>
          ))}
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-auto">
          {sortedCategories.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No capabilities match your search
            </div>
          ) : (
            sortedCategories.map((category) => (
              <div key={category} id={sectionId(category)} className="scroll-mt-4">
                {/* Category Header */}
                <div className="grid grid-cols-[1fr_100px_100px_100px_100px] bg-muted/30 border-b">
                  <div className="p-2 px-3 font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    {category}
                  </div>
                  <div className="col-span-4" />
                </div>

                {/* Capability Rows */}
                {filteredGroups[category].map((cap) => (
                  <div
                    key={cap.key}
                    className="grid grid-cols-[1fr_100px_100px_100px_100px] border-b hover:bg-muted/20 transition-colors"
                  >
                    <div className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{cap.label}</span>
                        {cap.isProtected && (
                          <span title="Protected capability">
                            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                          </span>
                        )}
                      </div>
                      {cap.description && (
                        <p className="text-xs text-muted-foreground mt-0.5">{cap.description}</p>
                      )}
                      <code className="text-xs text-muted-foreground/70 mt-1 block">{cap.key}</code>
                    </div>

                    {ROLE_ORDER.map((role) => {
                      const allowed = permissionMatrix.matrix[role]?.[cap.key] ?? false;

                      return (
                        <div key={role} className="p-3 flex items-center justify-center">
                          <Checkbox
                            checked={allowed}
                            onCheckedChange={() => handleToggle(role, cap.key, allowed, cap.isProtected)}
                            disabled={updateCapability.isPending}
                            className={allowed ? 'data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600' : ''}
                          />
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex-shrink-0 flex items-center gap-6 text-sm text-muted-foreground px-6 pb-4">
        <div className="flex items-center gap-2">
          <Check className="h-4 w-4 text-green-500" />
          <span>Allowed</span>
        </div>
        <div className="flex items-center gap-2">
          <X className="h-4 w-4 text-muted-foreground" />
          <span>Denied</span>
        </div>
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <span>Protected (critical system capability)</span>
        </div>
      </div>

      {/* Confirmation Dialog for Protected Capabilities */}
      <AlertDialog open={!!pendingChange} onOpenChange={(open) => !open && setPendingChange(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Modify Protected Capability?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to {pendingChange?.allowed ? 'enable' : 'disable'} a protected capability for{' '}
              <strong>{pendingChange && ROLE_LABELS[pendingChange.role]}</strong>.
              <br /><br />
              Protected capabilities are critical for system administration. Changing them may affect 
              system security or lock you out of important functions.
              <br /><br />
              <strong>Capability:</strong> {pendingChange?.capabilityKey}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPendingChange} className="bg-amber-600 hover:bg-amber-700">
              Confirm Change
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
